<script setup>
import { computed } from 'vue'

/**
 * Reusable RideSnap headline. Locks down the visual language across the app:
 * always teko-font, always uppercase, always tight tracking - but lets the
 * caller dial in size + accent treatment via tokens instead of free-form
 * Tailwind soup.
 *
 * Three composable accents (any combination):
 *   - `eyebrow`        - small mono-uppercase prefix, sits above the headline
 *                        ("GALERIE", "KOMUNITA · NÁVRH", etc.)
 *   - `highlight`      - second word/phrase rendered in primary-container
 *                        orange. Use `highlightBlock` to force it onto its
 *                        own line for the stacked "POPULÁRNÍ / ÚSEKY" look.
 *   - `gradient`       - white→zinc-500 vertical bg-clip gradient instead of
 *                        a flat color. Subtle, used for section headers like
 *                        "NEJNOVĚJŠÍ GALERIE".
 *
 * Color override via `color` prop: pass any Tailwind text-* class to override
 * the default `text-on-surface` on the inner heading element. Used for danger
 * zones (`color="text-red-400"`), muted headings (`color="text-on-surface-variant"`),
 * or images-overlay headings (`color="text-white"`). `gradient` mode wins -
 * passing `color` together with `gradient` is ignored.
 *
 * Semantic level (`as`) is decoupled from visual size (`size`) so an `<h1>`
 * can be small and an `<h3>` can be hero-sized when the page outline says so.
 *
 * Default text comes from the default slot if no `text` prop is supplied,
 * which is the easier mode for headlines that need inline markup.
 *
 * Examples:
 *   <Heading size="hero" highlight="Rychlost" highlight-italic>Najdi svou</Heading>
 *   <Heading size="md" eyebrow="Komunita · Návrh" highlight="Nové místo">Navrhnout</Heading>
 *   <Heading as="h2" size="md" gradient>Nejnovější galerie</Heading>
 *   <Heading as="h2" size="sm" color="text-red-400">Smazat účet</Heading>
 */
const props = defineProps({
    as: {
        type: String,
        default: 'h2',
        validator: (v) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'p'].includes(v),
    },
    size: {
        type: String,
        default: 'md',
        validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl', 'hero'].includes(v),
    },
    weight: {
        type: String,
        default: 'black',
        validator: (v) => ['bold', 'black'].includes(v),
    },
    align: {
        type: String,
        default: 'left',
        validator: (v) => ['left', 'center', 'right'].includes(v),
    },
    text: {
        type: String,
        default: '',
    },
    eyebrow: {
        type: String,
        default: '',
    },
    highlight: {
        type: String,
        default: '',
    },
    highlightBlock: {
        type: Boolean,
        default: false,
    },
    highlightItalic: {
        type: Boolean,
        default: false,
    },
    gradient: {
        type: Boolean,
        default: false,
    },
    color: {
        type: String,
        default: '',
    },
})

// Responsive size scale - chosen to match the ranges already in the codebase
// (auth h1 = ~text-5xl, page h1 ≈ md:text-5xl, hero ≈ md:text-9xl).
const SIZE_CLASSES = {
    xs: 'text-2xl',
    sm: 'text-3xl md:text-4xl',
    md: 'text-4xl md:text-5xl',
    lg: 'text-5xl md:text-6xl lg:text-7xl',
    xl: 'text-5xl md:text-7xl lg:text-8xl',
    hero: 'text-6xl md:text-8xl lg:text-9xl',
}

const EYEBROW_SIZE_CLASSES = {
    xs: 'text-[10px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-xs',
    xl: 'text-sm',
    hero: 'text-sm',
}

const ALIGN_CLASSES = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
}

const WEIGHT_CLASSES = {
    bold: 'font-bold',
    black: 'font-black',
}

// Gradient mode swaps text color for a clipped background gradient. Note the
// white-to-zinc-500 stop range is shallow on purpose - too steep and it looks
// like the headline is fading out mid-word.
//
// Resolution order: gradient > explicit color prop > on-surface default.
const baseTextClass = computed(() => {
    if (props.gradient) return 'bg-linear-to-b from-white to-zinc-500 bg-clip-text text-transparent'
    if (props.color) return props.color
    return 'text-on-surface'
})

const eyebrowAlignClass = computed(() => {
    if (props.align === 'center') return 'justify-center'
    if (props.align === 'right') return 'justify-end'
    return 'justify-start'
})
</script>

<template>
    <div
        class="space-y-3"
        :class="ALIGN_CLASSES[align]"
    >
        <!-- Eyebrow: small uppercase mono prefix with an orange accent line.
             The flex wrapper keeps the line and text vertically centered. -->
        <div
            class="flex items-center gap-3"
            v-if="eyebrow"
            :class="eyebrowAlignClass"
        >
            <span
                class="bg-primary-container/80 h-px w-10"
                v-if="align !== 'right'"
            />
            <span
                class="text-primary-container font-mono font-bold tracking-[0.25em] uppercase"
                :class="EYEBROW_SIZE_CLASSES[size]"
            >
                {{ eyebrow }}
            </span>
            <span
                class="bg-primary-container/80 h-px w-10"
                v-if="align === 'right'"
            />
        </div>

        <!-- Headline. Renders as the requested semantic element via :is. -->
        <component
            class="teko-font leading-[0.95] tracking-tighter uppercase"
            :is="as"
            :class="[SIZE_CLASSES[size], WEIGHT_CLASSES[weight], baseTextClass]"
        >
            <slot>{{ text }}</slot>
            <span
                class="text-primary-container"
                v-if="highlight"
                :class="[highlightBlock ? 'block' : 'ml-2', highlightItalic ? 'italic' : '']"
            >
                {{ highlight }}
            </span>
        </component>
    </div>
</template>
